/**
 * Input Component - CSS-First Design Token Architecture
 *
 * WCAG 2.1 AA compliant input with proper focus states.
 * Uses CSS classes for states instead of JavaScript handlers.
 */

import { InputHTMLAttributes, useId } from 'react'

// Omit the native 'size' attribute to avoid conflict with our size variant
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text */
  label?: string
  /** Error message (shows error state) */
  error?: string
  /** Helper text (hidden when error present) */
  helperText?: string
  /** Full width input */
  fullWidth?: boolean
  /** Input size variant (sm, md, lg) */
  inputSize?: 'sm' | 'md' | 'lg'
  /** Native HTML size attribute (character width) */
  htmlSize?: number
}

export function Input({
  label,
  error,
  helperText,
  fullWidth = false,
  inputSize = 'md',
  htmlSize,
  className = '',
  id,
  ...props
}: InputProps) {
  // Generate unique ID if not provided
  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = `${inputId}-error`
  const helperId = `${inputId}-helper`

  // Build class strings
  const wrapperClasses = [
    'input-wrapper',
    fullWidth ? 'input-wrapper--full-width' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const inputClasses = [
    'input',
    `input--${inputSize}`,
    error ? 'input--error' : '',
    fullWidth ? 'input--full-width' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={wrapperClasses}>
      {label && (
        <label
          htmlFor={inputId}
          className="input__label"
        >
          {label}
          {props.required && <span className="input__required" aria-hidden="true">*</span>}
        </label>
      )}
      <input
        id={inputId}
        className={inputClasses}
        size={htmlSize}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={
          error ? errorId : helperText ? helperId : undefined
        }
        {...props}
      />
      {error && (
        <p id={errorId} className="input__error" role="alert">
          <svg
            className="input__error-icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={helperId} className="input__helper">
          {helperText}
        </p>
      )}
    </div>
  )
}
